import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import '../../styles/email-verification.less';
import TechSmithLogo from '../../img/techsmith-logo.svg';
import { ControlButton, TscButtonType } from '@techsmith/tsc-cloud-style-guide';
import { getSearchParamsFromHref, redirectToUrl } from '../../../util/PageUtil';
import Config from '../../../config/config';
import { useTranslation } from 'react-i18next';
import getEmailVerificationStatus from '../../../api/email-verification/EmailVerificationStatusApi';
const POLL_INITIAL_DELAY_MS = 3000;
const POLL_MAX_DELAY_MS = 60000;
const POLL_MAX_RETRIES = 10;
const getNextPollDelay = (attempt) => Math.min(POLL_INITIAL_DELAY_MS * Math.pow(2, attempt), POLL_MAX_DELAY_MS);
const EmailVerificationPage = () => {
    const { t } = useTranslation();
    const [emailToVerify, setEmailToVerify] = useState();
    const [signInUrl, setSignInUrl] = useState();
    const [continueUrl, setContinueUrl] = useState('');
    const [showContinueButton, setShowContinueButton] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const [isPollingStopped, setIsPollingStopped] = useState(false);
    const techSmithIdRef = useRef(null);
    const pollAttemptRef = useRef(0);
    const pollTimerRef = useRef(null);
    const isMountedRef = useRef(true);
    const pollVerificationStatus = () => {
        const techSmithId = techSmithIdRef.current;
        if (!techSmithId) {
            return;
        }
        const scheduleNext = () => {
            const attempt = pollAttemptRef.current;
            if (attempt >= POLL_MAX_RETRIES) {
                setIsPollingStopped(true);
                return;
            }
            pollAttemptRef.current = attempt + 1;
            pollTimerRef.current = setTimeout(pollVerificationStatus, getNextPollDelay(attempt));
        };
        getEmailVerificationStatus(techSmithId)
            .then(result => {
            if (!isMountedRef.current) {
                return;
            }
            if (result.IsVerified) {
                setIsVerified(true);
            }
            else {
                scheduleNext();
            }
        })
            .catch(() => {
            if (isMountedRef.current) {
                scheduleNext();
            }
        });
    };
    useEffect(() => {
        var _a, _b, _c;
        isMountedRef.current = true;
        document.title = t('emailVerification.pageTitle');
        const urlParams = getSearchParamsFromHref();
        const email = urlParams.get('email');
        const techSmithId = urlParams.get('techSmithId');
        const defaultEmail = t('emailVerification.yourEmail');
        const auth0Url = (_a = Config.auth0BaseUrl) !== null && _a !== void 0 ? _a : 'https://login.techsmith.com/';
        const state = (_b = urlParams.get('state')) !== null && _b !== void 0 ? _b : '';
        const loginQueryState = (_c = sessionStorage.getItem('query.state')) !== null && _c !== void 0 ? _c : state;
        setEmailToVerify(email !== null && email !== void 0 ? email : defaultEmail);
        setSignInUrl(`${auth0Url}u/login/identifier?state=${loginQueryState}`);
        if (techSmithId) {
            setShowContinueButton(true);
            setContinueUrl(`${auth0Url}continue?state=${loginQueryState}`);
            techSmithIdRef.current = techSmithId;
            pollTimerRef.current = setTimeout(pollVerificationStatus, POLL_INITIAL_DELAY_MS);
        }
        return () => {
            isMountedRef.current = false;
            if (pollTimerRef.current !== null) {
                clearTimeout(pollTimerRef.current);
            }
        };
    }, []);
    return (_jsx("div", Object.assign({ className: "email-verification-container" }, { children: _jsxs("div", Object.assign({ className: "content-main" }, { children: [_jsx(TechSmithLogo, {}), _jsx("div", { children: _jsxs("section", Object.assign({ id: "noSignInMessage", className: "content" }, { children: [_jsx("h2", { children: t('emailVerification.verifyEmailTitle') }), _jsxs("p", { children: [t('emailVerification.emailSentBeforeText'), emailToVerify, t('emailVerification.emailSentAfterText')] }), _jsxs("p", { children: [t('emailVerification.alreadyVerified'), " ", _jsx("strong", { children: _jsx("a", Object.assign({ href: signInUrl }, { children: t('emailVerification.signIn') })) })] })] })) }), showContinueButton && (_jsxs("section", Object.assign({ className: "action-content" }, { children: [!isVerified && !isPollingStopped && (_jsx("p", Object.assign({ className: "verification-status" }, { children: t('emailVerification.waitingForVerification') }))), !isVerified && isPollingStopped && (_jsx("p", Object.assign({ className: "poll-error" }, { children: t('emailVerification.verificationCheckFailed') }))), _jsx(ControlButton, { buttonType: TscButtonType.primary, label: t('emailVerification.continue'), title: t('emailVerification.continue'), className: "btn-continue", disabled: !isVerified, onClick: () => {
                                redirectToUrl(continueUrl);
                            } })] })))] })) })));
};
export default EmailVerificationPage;
